<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto"
  >
    <div
      class="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transform transition-all my-8"
    >
      <!-- Header -->
      <div
        class="px-6 py-5 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white flex items-center justify-between"
      >
        <div class="flex items-center space-x-3">
          <div
            class="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20"
          >
            <CreditCard class="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 class="text-lg font-bold">Record Fee Payment</h3>
            <p class="text-xs text-emerald-100 font-medium">
              Accept full or partial fee payments with instant receipt
            </p>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="p-2 text-white/80 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Student Info Banner -->
      <div
        v-if="student"
        class="bg-emerald-50/70 border-b border-emerald-100 px-6 py-3 flex items-center justify-between"
      >
        <div>
          <span class="text-xs font-semibold text-emerald-600 uppercase tracking-wider"
            >Paying Student</span
          >
          <h4 class="text-sm font-bold text-slate-800">{{ student.name }}</h4>
        </div>
        <div class="text-right">
          <span class="text-xs text-slate-500">Roll: {{ student.rollNumber || "N/A" }}</span>
          <p class="text-xs font-medium text-slate-700">
            {{ student.class?.name }}
            {{ student.class?.section ? `(${student.class.section})` : "" }}
          </p>
        </div>
      </div>

      <!-- Form Body -->
      <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
        <!-- Category Selector -->
        <div>
          <label class="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">
            Fee Category
          </label>
          <div class="grid grid-cols-2 gap-3">
            <button
              type="button"
              @click="setCategory('monthly')"
              :class="[
                'p-3 rounded-xl border-2 text-sm font-semibold transition-all flex items-center justify-center gap-2',
                form.feeCategory === 'monthly'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
              ]"
            >
              <Calendar class="w-4 h-4 text-emerald-600" />
              Monthly Fee
            </button>
            <button
              type="button"
              @click="setCategory('admission')"
              :class="[
                'p-3 rounded-xl border-2 text-sm font-semibold transition-all flex items-center justify-center gap-2',
                form.feeCategory === 'admission'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
              ]"
            >
              <GraduationCap class="w-4 h-4 text-indigo-600" />
              Admission Fee
            </button>
          </div>
        </div>

        <!-- If Monthly: Select Month -->
        <div v-if="form.feeCategory === 'monthly'">
          <label class="block text-xs font-semibold text-slate-700 mb-1">
            Select Billing Month <span class="text-red-500">*</span>
          </label>
          <select
            v-model="form.monthlyFeeRecordId"
            @change="handleMonthChange"
            required
            class="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-emerald-500 outline-none"
          >
            <option value="" disabled>-- Select Month --</option>
            <option
              v-for="rec in payableMonthlyRecords"
              :key="rec._id"
              :value="rec._id"
            >
              {{ rec.monthName }} {{ rec.year }} — Due: ₹{{ rec.balance.toLocaleString("en-IN") }}
              {{ rec.status === "overdue" ? "(Overdue)" : "" }}
            </option>
          </select>
          <p
            v-if="payableMonthlyRecords.length === 0"
            class="text-xs text-amber-600 mt-1"
          >
            All generated monthly fees have been fully paid.
          </p>
        </div>

        <!-- Balance Info Banner -->
        <div
          class="p-3.5 rounded-xl border flex items-center justify-between"
          :class="
            currentBalance > 0
              ? 'bg-amber-50/70 border-amber-200 text-amber-900'
              : 'bg-slate-50 border-slate-200 text-slate-700'
          "
        >
          <div>
            <span class="text-xs font-medium text-slate-500 block">
              Remaining Outstanding Balance
            </span>
            <span class="text-lg font-extrabold">
              ₹{{ currentBalance.toLocaleString("en-IN") }}
            </span>
          </div>
          <button
            v-if="currentBalance > 0"
            type="button"
            @click="payFullBalance"
            class="px-2.5 py-1 text-xs font-bold text-emerald-700 bg-emerald-100/80 hover:bg-emerald-200 rounded-lg transition"
          >
            Fill Full Balance
          </button>
        </div>

        <!-- Amount Received -->
        <div>
          <label class="block text-xs font-semibold text-slate-700 mb-1">
            Amount Received (₹) <span class="text-red-500">*</span>
          </label>
          <div class="relative">
            <span class="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400 font-bold">₹</span>
            <input
              v-model.number="form.amount"
              type="number"
              min="1"
              :max="currentBalance > 0 ? currentBalance : undefined"
              step="any"
              required
              placeholder="0.00"
              class="w-full pl-8 pr-4 py-2.5 text-base font-bold rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 outline-none transition"
            />
          </div>
          <p v-if="validationError" class="text-xs text-red-500 mt-1 font-medium">
            {{ validationError }}
          </p>
        </div>

        <!-- Payment Date & Method -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-700 mb-1">
              Payment Date
            </label>
            <input
              v-model="form.paymentDate"
              type="date"
              required
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 outline-none"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-700 mb-1">
              Payment Method <span class="text-red-500">*</span>
            </label>
            <select
              v-model="form.paymentMethod"
              required
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-emerald-500 outline-none"
            >
              <option value="Cash">Cash</option>
              <option value="UPI">UPI (GPay / PhonePe / Paytm)</option>
              <option value="Bank transfer">Bank Transfer / NEFT / IMPS</option>
              <option value="Card">Debit / Credit Card</option>
              <option value="Cheque">Cheque</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <!-- Reference & Remarks -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-700 mb-1">
              Transaction / Cheque Ref
            </label>
            <input
              v-model="form.transactionReference"
              type="text"
              placeholder="e.g. UTR123456 or Chq #001"
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 outline-none"
            />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-700 mb-1">
              Remarks (Optional)
            </label>
            <input
              v-model="form.remarks"
              type="text"
              placeholder="e.g. Paid by father"
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 outline-none"
            />
          </div>
        </div>

        <!-- Buttons -->
        <div class="pt-4 flex items-center justify-end space-x-3 border-t border-slate-200">
          <button
            type="button"
            @click="$emit('close')"
            :disabled="isSubmitting"
            class="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSubmitting || !canSubmit"
            class="px-5 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-xl shadow-lg shadow-emerald-500/25 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span
              v-if="isSubmitting"
              class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
            ></span>
            <CheckCircle2 v-else class="w-4 h-4" />
            <span>{{ isSubmitting ? "Recording..." : "Record & Print Receipt" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { reactive, computed, watch } from "vue";
  import {
    X,
    CreditCard,
    Calendar,
    GraduationCap,
    CheckCircle2,
  } from "lucide-vue-next";

  const props = defineProps({
    show: { type: Boolean, default: false },
    student: { type: Object, default: null },
    feeStructure: { type: Object, default: null },
    monthlyRecords: { type: Array, default: () => [] },
    preselectedCategory: { type: String, default: "monthly" },
    preselectedMonthlyRecordId: { type: String, default: "" },
    isSubmitting: { type: Boolean, default: false },
  });

  const emit = defineEmits(["close", "submit"]);

  const form = reactive({
    feeCategory: "monthly",
    monthlyFeeRecordId: "",
    amount: null,
    paymentDate: new Date().toISOString().substring(0, 10),
    paymentMethod: "Cash",
    transactionReference: "",
    remarks: "",
  });

  // Filter only monthly records with outstanding balance
  const payableMonthlyRecords = computed(() => {
    return props.monthlyRecords.filter((rec) => rec.balance > 0);
  });

  // Calculate current outstanding balance for the selected category
  const currentBalance = computed(() => {
    if (form.feeCategory === "admission") {
      return props.feeStructure?.admissionFee?.balance || 0;
    }
    const target = props.monthlyRecords.find(
      (r) => r._id === form.monthlyFeeRecordId
    );
    return target ? target.balance : 0;
  });

  const validationError = computed(() => {
    if (!form.amount || form.amount <= 0) {
      return "Amount must be greater than zero.";
    }
    if (form.amount > currentBalance.value) {
      return `Amount exceeds the outstanding balance of ₹${currentBalance.value.toLocaleString("en-IN")}.`;
    }
    return null;
  });

  const canSubmit = computed(() => {
    if (!form.amount || form.amount <= 0) return false;
    if (form.amount > currentBalance.value) return false;
    if (form.feeCategory === "monthly" && !form.monthlyFeeRecordId) return false;
    return true;
  });

  const setCategory = (cat) => {
    form.feeCategory = cat;
    if (cat === "admission") {
      form.amount = currentBalance.value > 0 ? currentBalance.value : null;
    } else {
      if (payableMonthlyRecords.value.length > 0) {
        form.monthlyFeeRecordId = payableMonthlyRecords.value[0]._id;
        form.amount = payableMonthlyRecords.value[0].balance;
      } else {
        form.monthlyFeeRecordId = "";
        form.amount = null;
      }
    }
  };

  const handleMonthChange = () => {
    const target = props.monthlyRecords.find(
      (r) => r._id === form.monthlyFeeRecordId
    );
    if (target) {
      form.amount = target.balance;
    }
  };

  const payFullBalance = () => {
    form.amount = currentBalance.value;
  };

  // Watch for opening or preselection
  watch(
    () => props.show,
    (isShown) => {
      if (isShown) {
        form.paymentDate = new Date().toISOString().substring(0, 10);
        form.feeCategory = props.preselectedCategory || "monthly";

        if (form.feeCategory === "monthly") {
          if (props.preselectedMonthlyRecordId) {
            form.monthlyFeeRecordId = props.preselectedMonthlyRecordId;
            const target = props.monthlyRecords.find(
              (r) => r._id === props.preselectedMonthlyRecordId
            );
            form.amount = target ? target.balance : null;
          } else if (payableMonthlyRecords.value.length > 0) {
            form.monthlyFeeRecordId = payableMonthlyRecords.value[0]._id;
            form.amount = payableMonthlyRecords.value[0].balance;
          } else {
            form.monthlyFeeRecordId = "";
            form.amount = null;
          }
        } else {
          form.amount = props.feeStructure?.admissionFee?.balance || null;
        }

        form.transactionReference = "";
        form.remarks = "";
      }
    },
    { immediate: true }
  );

  const handleSubmit = () => {
    if (!canSubmit.value) return;

    // Generate unique idempotency key to protect against double click / network retry
    const idempotencyKey = `pay_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    emit("submit", {
      feeCategory: form.feeCategory,
      monthlyFeeRecordId:
        form.feeCategory === "monthly" ? form.monthlyFeeRecordId : null,
      amount: form.amount,
      paymentDate: form.paymentDate,
      paymentMethod: form.paymentMethod,
      transactionReference: form.transactionReference.trim(),
      remarks: form.remarks.trim(),
      idempotencyKey,
    });
  };
</script>
